import { isEqual, range } from 'lodash-es';
import { BehaviorSubject, ReplaySubject, combineLatest, firstValueFrom, timer, } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { asString, hexString, paddedHex, parseHexString } from '../util/misc.js';
import { AsyncMulticastSubject } from '../util/rx.js';
import { FairServiceScheduler } from '../util/sched.js';
import { EFBIG, EMSGSIZE, EPIPE, LoraxError, LoraxOpenFlags, getLoraxErrorCode, isDisconnectedLoraxErrorCode, isRetriableLoraxErrorCode, loraxWatchEventOverhead, pikaUuid, } from './connection-manager.js';
import { BleDeviceError } from './interface.js';
import { defs } from './params.js';
import { pikachuService } from './uuids.js';
import { revisionNumberToString, revisionStringToNumber } from './version-string.js';
const errorMessages = {
    noCharParamWatch: 'Param backed by char does not support watch',
    noIndexedParamWatch: 'Indexed char param does not support watch',
};
export class ParamError extends Error {
    cause;
    static causeFromAny(err) {
        return err instanceof ParamError ? err.cause : undefined;
    }
    constructor(cause) {
        super(errorMessages[cause]);
        this.cause = cause;
    }
}
export var AnimationCode;
(function (AnimationCode) {
    AnimationCode[AnimationCode["Preserve"] = 0] = "Preserve";
    AnimationCode[AnimationCode["AllOn"] = 1] = "AllOn";
    AnimationCode[AnimationCode["AllOff"] = 2] = "AllOff";
    AnimationCode[AnimationCode["BondingSuccessful"] = 3] = "BondingSuccessful";
    AnimationCode[AnimationCode["ConnectSuccessful"] = 4] = "ConnectSuccessful";
    AnimationCode[AnimationCode["Breathing"] = 5] = "Breathing";
    AnimationCode[AnimationCode["Rising"] = 6] = "Rising";
    AnimationCode[AnimationCode["Circling"] = 7] = "Circling";
    AnimationCode[AnimationCode["HeatCycleActive"] = 8] = "HeatCycleActive";
    AnimationCode[AnimationCode["TempSelect"] = 9] = "TempSelect";
    AnimationCode[AnimationCode["ChargeStart"] = 10] = "ChargeStart";
    AnimationCode[AnimationCode["Charging"] = 11] = "Charging";
    AnimationCode[AnimationCode["ChargingLow"] = 12] = "ChargingLow";
    AnimationCode[AnimationCode["IdleWakeup"] = 13] = "IdleWakeup";
    AnimationCode[AnimationCode["IdleToOff"] = 14] = "IdleToOff";
    AnimationCode[AnimationCode["HeatCycleFade"] = 15] = "HeatCycleFade";
    AnimationCode[AnimationCode["StealthEngage"] = 16] = "StealthEngage";
    AnimationCode[AnimationCode["StealthDisengage"] = 17] = "StealthDisengage";
    AnimationCode[AnimationCode["LogoOnly"] = 18] = "LogoOnly";
    AnimationCode[AnimationCode["FactoryReset"] = 19] = "FactoryReset";
    AnimationCode[AnimationCode["Bonding"] = 20] = "Bonding";
    AnimationCode[AnimationCode["User0"] = 240] = "User0";
    AnimationCode[AnimationCode["User1"] = 241] = "User1";
    AnimationCode[AnimationCode["User2"] = 242] = "User2";
    AnimationCode[AnimationCode["User3"] = 243] = "User3";
    AnimationCode[AnimationCode["User0To1"] = 244] = "User0To1";
    AnimationCode[AnimationCode["User2To3"] = 245] = "User2To3";
    AnimationCode[AnimationCode["User0To3"] = 246] = "User0To3";
    AnimationCode[AnimationCode["User4"] = 247] = "User4";
    AnimationCode[AnimationCode["User5"] = 248] = "User5";
    AnimationCode[AnimationCode["User4To5"] = 249] = "User4To5";
})(AnimationCode || (AnimationCode = {}));
export class RgbtColor {
    static fromRgb(p) {
        return new RgbtColor([p.r, p.g, p.b, 0, p.lumaAnim ?? 0, 0, 0, 0]);
    }
    static fromTable(p) {
        const colorLength = p.colorLength ?? 32;
        return new RgbtColor([
            p.luma,
            p.speed,
            p.lumaAnim ?? 0,
            1,
            p.pllNum ?? 0,
            p.pllDenom ?? 0,
            (p.offsetIndex << 4) | p.colorIndex,
            colorLength < 32 ? colorLength : 0,
        ]);
    }
    static fromNull() {
        return new RgbtColor([0, 0, 0, 0xff, 0, 0, 0, 0]);
    }
    static from4Byte(b) {
        if (b.byteLength !== 4) {
            throw new RangeError('RgbtColor encoded form must be 4 bytes');
        }
        const a = new Uint8Array(b);
        if (a[3] === 0) {
            return RgbtColor.fromRgb({ r: a[0], g: a[1], b: a[2] });
        }
        else {
            return RgbtColor.fromTable({
                luma: a[0],
                speed: a[1],
                offsetIndex: a[3] <= 0x8 ? a[3] - 1 : 0xff - a[3] + 0x8,
                colorIndex: a[2] <= 0x7 ? a[2] : 0xff - a[2] + 0x8,
            });
        }
    }
    bytes;
    constructor(bytes) {
        if (bytes.length !== 8) {
            throw new RangeError('RgbtColor encoded form must be 8 bytes');
        }
        this.bytes = new Uint8Array(bytes);
    }
    get isRgb() {
        return this.bytes[3] === 0;
    }
    get isTable() {
        return this.bytes[3] === 1;
    }
    get isNull() {
        return this.bytes[3] === 0xff;
    }
    get asRgb() {
        if (this.bytes[3] !== 0) {
            return undefined;
        }
        return { r: this.bytes[0], g: this.bytes[1], b: this.bytes[2], lumaAnim: this.bytes[4] };
    }
    get asTable() {
        if (this.bytes[3] !== 1) {
            return undefined;
        }
        const colorLengthRaw = this.bytes[7] & 0x1f;
        return {
            luma: this.bytes[0],
            speed: this.bytes[1],
            lumaAnim: this.bytes[2],
            pllNum: this.bytes[4],
            pllDenom: this.bytes[5],
            offsetIndex: this.bytes[6] >> 4,
            colorIndex: this.bytes[6] & 0xf,
            colorLength: colorLengthRaw || 32,
        };
    }
    get lumaAnim() {
        return this.asRgb?.lumaAnim ?? this.asTable?.lumaAnim;
    }
    get as4Byte() {
        if (this.asRgb) {
            const rgb = this.asRgb;
            return new Uint8Array([rgb.r, this.asRgb.g, this.asRgb.b, 0]).buffer;
        }
        if (this.asTable) {
            const table = this.asTable;
            return new Uint8Array([
                table.luma,
                table.speed,
                table.colorIndex <= 0x7 ? table.colorIndex : 0xff - (table.colorIndex - 0x8),
                table.offsetIndex <= 0x7 ? table.offsetIndex + 1 : 0xff - (table.offsetIndex - 0x8),
            ]).buffer;
        }
        return new Uint8Array([0x7f, 0x7f, 0x7f, 0x7f]).buffer;
    }
}
/**
 * Convert an HTML color into an RgbColor
 * @param htmlColor HTML color, optionally starting with `#`
 * @param mode 'strict' to require exactly 6 hex digits, 'relaxed' to allow leading zeroes to be dropped
 * @returns RgbColor
 * @throws If `htmlColor` doesn't meet the requirements
 */
export const parseHtmlColor = (htmlColor, mode = 'strict') => {
    let digits;
    if (mode === 'strict') {
        const match = /^#?([0-9a-fA-F]{1,6})$/.exec(htmlColor)?.[1];
        if (!match) {
            throw new Error(`Color string '${htmlColor}' is not in HTML format`);
        }
        digits = match;
    }
    else {
        const match = /^#?([0-9a-fA-F]{1,6})$/.exec(htmlColor)?.[1];
        if (!match) {
            throw new Error(`Color string '${htmlColor}' is not in relaxed HTML format`);
        }
        digits = `00000${match}`.slice(-6);
    }
    return {
        r: parseInt(digits.slice(0, 2), 16),
        g: parseInt(digits.slice(2, 4), 16),
        b: parseInt(digits.slice(4, 6), 16),
    };
};
export const parseHtmlColorStrict = (htmlColor) => parseHtmlColor(htmlColor, 'strict');
export const parseHtmlColorRelaxed = (htmlColor) => parseHtmlColor(htmlColor, 'relaxed');
export const unparseHtmlColor = (color) => `#${paddedHex(color.r, 2)}${paddedHex(color.g, 2)}${paddedHex(color.b, 2)}`;
/** Maximum number of LEDs supported by Peak Pro */
export const maxLedSize = 20;
const animLedLumaSize = 1;
const animTimeSize = 4;
const animFrameSize = animTimeSize + maxLedSize * animLedLumaSize;
const numberCodec = {
    encodeJson: (value) => value,
    decodeJson: (value) => {
        if (typeof value !== 'number') {
            throw new TypeError(`${asString(value)} is not a number`);
        }
        return value ?? NaN;
    },
};
const numberArrayCodec = {
    encodeJson: (value) => value,
    decodeJson: (value) => {
        if (!Array.isArray(value)) {
            throw new TypeError(`${asString(value)} is not an array`);
        }
        if (!value.every((v) => typeof v === 'number')) {
            throw new TypeError(`${asString(value)} has an element that is not a number`);
        }
        return value.map((v) => v ?? NaN);
    },
};
const isAnimFrame = (v) => typeof v === 'object' &&
    v !== null &&
    'time' in v &&
    'luma' in v &&
    typeof v.time === 'number' &&
    v.time > 0 &&
    Array.isArray(v.luma) &&
    v.luma.every((e) => typeof e === 'number' && e >= 0 && e <= 255);
export const codecs = {
    int8: {
        encode: (value) => new Int8Array([value]).buffer,
        decode: (buffer) => {
            if (buffer.byteLength !== 1) {
                throw new RangeError('buffer length for int8 should be 1');
            }
            return new Int8Array(buffer)[0];
        },
        ...numberCodec,
    },
    uint8: {
        encode: (value) => new Uint8Array([value]).buffer,
        decode: (buffer) => {
            if (buffer.byteLength !== 1) {
                throw new RangeError('buffer length for uint8 should be 1');
            }
            return new Uint8Array(buffer)[0];
        },
        ...numberCodec,
    },
    uint8Array: {
        encode: (value) => new Uint8Array(value).buffer,
        decode: (buffer) => Array.from(new Uint8Array(buffer)),
        ...numberArrayCodec,
    },
    uint32: {
        encode: (value) => new Uint32Array([value]).buffer,
        decode: (buffer) => {
            if (buffer.byteLength !== 4) {
                throw new RangeError('buffer length for uint32 should be 4');
            }
            return new Uint32Array(buffer)[0];
        },
        ...numberCodec,
    },
    uint32Array: {
        encode: (value) => new Uint32Array(value).buffer,
        decode: (buffer) => Array.from(new Uint32Array(buffer)),
        ...numberArrayCodec,
    },
    float32: {
        encode: (value) => new Float32Array([value]).buffer,
        decode: (buffer) => {
            if (buffer.byteLength !== 4) {
                throw new RangeError('buffer length for float32 should be 4');
            }
            return new Float32Array(buffer)[0];
        },
        ...numberCodec,
    },
    float32Array: {
        encode: (value) => new Float32Array(value).buffer,
        decode: (buffer) => Array.from(new Float32Array(buffer)),
        ...numberArrayCodec,
    },
    string: {
        encode: (value) => new TextEncoder().encode(value).buffer,
        decode: (buffer) => new TextDecoder().decode(buffer),
        encodeJson: (value) => value,
        decodeJson: (value) => {
            if (typeof value !== 'string') {
                throw new TypeError(`${asString(value)} is not a string`);
            }
            return value;
        },
    },
    decimalString: {
        encode: (value) => new TextEncoder().encode(value.toString()).buffer,
        decode: (buffer) => parseFloat(new TextDecoder().decode(buffer)),
        encodeJson: (value) => value.toString(),
        decodeJson: (value) => {
            let num;
            if (typeof value !== 'string' || !isFinite((num = parseFloat(value)))) {
                throw new TypeError(`${asString(value)} is not a decimal string`);
            }
            return num;
        },
    },
    versionString: {
        encode: (value) => new TextEncoder().encode(revisionNumberToString(value)).buffer,
        decode: (buffer) => revisionStringToNumber(new TextDecoder().decode(buffer)),
        encodeJson: revisionNumberToString,
        decodeJson: (value) => {
            let num;
            if (typeof value !== 'string' || !isFinite((num = revisionStringToNumber(value)))) {
                throw new TypeError(`${asString(value)} is not a version string`);
            }
            return num;
        },
    },
    rgbtApi2: {
        encode: (value) => value.bytes.buffer,
        decode: (buffer) => new RgbtColor(new Uint8Array(buffer)),
        encodeJson: (value) => hexString(value.bytes),
        decodeJson: (value) => {
            const bytes = parseHexString(value);
            if (bytes.length !== 8) {
                throw new TypeError(`${asString(value)} is not a valid RgbtColor`);
            }
            return new RgbtColor(bytes);
        },
    },
    rgbtApi1: {
        encode: (value) => value.as4Byte,
        decode: (buffer) => RgbtColor.from4Byte(buffer),
        encodeJson: (value) => hexString(new Uint8Array(value.as4Byte)),
        decodeJson: (value) => {
            const bytes = parseHexString(value);
            if (bytes.length !== 4) {
                throw new TypeError(`${asString(value)} is not a valid RgbtColor`);
            }
            return RgbtColor.from4Byte(new Uint8Array(bytes).buffer);
        },
    },
    rgbArray: {
        encode: (value) => {
            const arr = new Array(value.length * 4);
            for (const [i, v] of value.entries()) {
                arr.splice(i * 4, 4, v.r, v.g, v.b, 0);
            }
            return new Uint8Array(arr).buffer;
        },
        decode: (buffer) => {
            if (buffer.byteLength % 4 !== 0) {
                throw new RangeError('buffer length for rgbArray should be a multiple of 4');
            }
            const arr = new Uint8Array(buffer);
            return range(0, arr.length, 4).map((i) => ({
                r: arr[i + 0],
                g: arr[i + 1],
                b: arr[i + 2],
            }));
        },
        encodeJson: (value) => value.map(unparseHtmlColor),
        decodeJson: (value) => {
            if (!Array.isArray(value)) {
                throw new TypeError(`${asString(value)} is not a valid RGB color array`);
            }
            return value.map(parseHtmlColorStrict);
        },
    },
    anim: {
        encode: (value) => {
            const buffer = new ArrayBuffer(value.length * animFrameSize);
            for (const [i, v] of value.entries()) {
                const view = new DataView(buffer, i * animFrameSize, animFrameSize);
                view.setFloat32(0, v.time, true);
                for (const [j, l] of v.luma.entries()) {
                    view.setUint8(animTimeSize + j, l);
                }
            }
            return buffer;
        },
        decode: (buffer) => {
            if (buffer.byteLength % animFrameSize !== 0) {
                throw new RangeError('buffer length for anim should be a multiple of ' + animFrameSize);
            }
            const view = new DataView(buffer);
            return range(0, buffer.byteLength, animFrameSize).map((i) => ({
                time: view.getFloat32(i, true),
                luma: Array.from(new Uint8Array(buffer.slice(i + animTimeSize, i + animFrameSize))),
            }));
        },
        encodeJson: (value) => value,
        decodeJson: (value) => {
            if (!Array.isArray(value) || !value.every(isAnimFrame)) {
                throw new TypeError(`${asString(value)} is not a valid animation array`);
            }
            return value.map((f) => ({ ...f, time: f.time ?? NaN }));
        },
    },
    led3ColorPtr: {
        encode: (value) => new Uint8Array([value.source, value.r, value.g, value.b]).buffer,
        decode: (buffer) => {
            if (buffer.byteLength !== 4) {
                throw new RangeError('Led3ColorPointer encoded form must be 4 bytes');
            }
            const a = new Uint8Array(buffer);
            return { source: a[0], r: a[1], g: a[2], b: a[3] };
        },
        encodeJson: (value) => value,
        decodeJson: (value) => {
            if (typeof value !== 'object' ||
                value === null ||
                !['source', 'r', 'g', 'b'].every((key) => key in value)) {
                throw new TypeError(`${asString(value)} is not a valid LED3 color pointer`);
            }
            return value;
        },
    },
    raw: {
        encode: (value) => value,
        decode: (value) => value,
        encodeJson: (value) => hexString(value),
        decodeJson: (value) => new Uint8Array(parseHexString(value)).buffer,
    },
};
const apiLimitToNumber = (str) => {
    if (typeof str === 'string') {
        return { default: revisionStringToNumber(str) };
    }
    const out = {
        default: 0, // placeholder, will be overwritten
    };
    for (const [key, value] of Object.entries(str)) {
        out[key] = revisionStringToNumber(value);
    }
    return out;
};
const errorHandling = (err) => {
    if (LoraxError.isInstance(err)) {
        return isDisconnectedLoraxErrorCode(err.code)
            ? 'failSilent'
            : isRetriableLoraxErrorCode(err.code)
                ? 'retry'
                : 'failNoisy';
    }
    const bleCause = BleDeviceError.causeFromAny(err);
    if (bleCause === 'charNotFound') {
        return 'failNoisy';
    }
    if (bleCause === 'notConnected') {
        return 'failSilent';
    }
    return 'retry';
};
/*
ParamRegistry has a map of all params; pages can request them by key.

ParamRegistry manages when params are read/written using a best-effort priority system.
It has a queue of tasks, each of which has a "frequency", nominally in units of Hz.
Tasks are enqueued by the params, and they get a timestamp of when they were first queued.
Tasks are then serviced according to a "figure of urgency", which is the time since queued,
multiplied by the frequency.

When device first connects, ParamRegistry tells each param (or perhaps this happens when the params
are subscribed and their value is unknown); they schedule a read with frequency
VERY_FAST. Pages displaying params subscribe to the param's observable; if the param is mutable,
it schedules reads with the frequency given in its definition. When the read completes, it gets
requeued unless all subscribers are gone.

If tasks fail, they are requeued with half the original frequency.
*/
const VERY_FAST = 1000000;
export class ParamAccessDeniedError extends Error {
    constructor(param, operation) {
        super(`Could not ${operation} ${param.key}: access denied`);
    }
}
class BaseParam {
    provider;
    key;
    attrs;
    encodings;
    codec;
    writing;
    monitorState;
    apiRange;
    monitorSubject;
    watchFile;
    constructor(def, provider) {
        this.provider = provider;
        this.key = def.key;
        this.attrs = def.attrs;
        this.encodings = def.encodings;
        this.apiRange = [
            def.minApi ? apiLimitToNumber(def.minApi) : { default: -Infinity },
            def.maxApi ? apiLimitToNumber(def.maxApi) : { default: Infinity },
        ];
        this.codec = codecs[def.dataType];
        this.monitorSubject = new AsyncMulticastSubject({
            start: () => this.monitor(this.attrs.freq),
            stop: () => this.unmonitor(),
            replay: true,
        });
    }
    /** Close all subscriptions */
    teardown() {
        if (this.monitorState) {
            this.unmonitor().catch((error) => this.provider.logger.warn(`Teardown failed`, { error }));
        }
    }
    get canRead() {
        return this.attrs.read;
    }
    get canWrite() {
        return this.attrs.write;
    }
    isInApi(version, productRaw) {
        const product = productRaw ?? 'default';
        const minApi = this.apiRange[0][product] ?? this.apiRange[0].default;
        const maxApi = this.apiRange[1][product] ?? this.apiRange[1].default;
        if (minApi === -Infinity && maxApi === Infinity) {
            return true;
        }
        if (version === undefined) {
            return undefined;
        }
        return version >= minApi && version <= maxApi;
    }
    async readOnce(freq) {
        return await this.queueRead(freq ?? VERY_FAST, 10);
    }
    async readOnceJson(freq) {
        const value = await this.readOnce(freq);
        return value === undefined ? undefined : this.codec.encodeJson(value);
    }
    async set(v, noReadback = false) {
        if (this.writing !== undefined) {
            this.writing = v;
        }
        else {
            try {
                return await this.queueWriteAndReadback(v, noReadback);
            }
            catch (error) {
                this.provider.logger.error('Set failed.', { error });
                throw error;
            }
        }
    }
    async setJson(v, noReadback = false) {
        const value = await this.set(this.codec.decodeJson(v), noReadback);
        return value === undefined ? undefined : this.codec.encodeJson(value);
    }
    async watch(freq) {
        if (this.path.type === 'char') {
            throw new ParamError('noCharParamWatch');
        }
        let newlyOpened = false;
        if (!this.watchFile?.isOpen) {
            newlyOpened = true;
            await this.queueOpen(freq ?? VERY_FAST, this.path.path);
        }
        try {
            return await this.queueWatch(freq ?? VERY_FAST, 10);
        }
        catch (err) {
            if (newlyOpened) {
                try {
                    await this.watchFile.close();
                }
                catch {
                    console.warn('Failed to close file after watch failed');
                }
            }
            throw err;
        }
    }
    async unwatch() {
        await this.queueUnwatch(10);
    }
    async startObs() {
        return await this.monitorSubject.get();
    }
    async monitor(freq) {
        if (this.monitorState) {
            throw new Error(`Attempted to start multiple monitor sessions on ${this.key}`);
        }
        if (!this.provider.disableWatch && freq) {
            try {
                const obs = await this.watch(freq);
                const subj = new ReplaySubject();
                let watchState = undefined;
                const cleanup = () => {
                    if (this.monitorState === watchState) {
                        this.monitorState = undefined;
                    }
                };
                const sub = obs.pipe(tap({ error: cleanup, complete: cleanup })).subscribe(subj);
                watchState = { type: 'watch', subj, sub };
                this.monitorState = watchState;
                return subj;
            }
            catch (error) {
                if (ParamError.causeFromAny(error) === 'noCharParamWatch' ||
                    ParamError.causeFromAny(error) === 'noIndexedParamWatch' ||
                    getLoraxErrorCode(error) === EFBIG) {
                    this.provider.logger.info(`Could not use Lorax watch for ${this.key}, falling back to polling`, { error });
                }
                else {
                    throw error;
                }
            }
        }
        // No Lorax watch (or no refresh), do a fast read and then poll if needed
        const loop = !!freq;
        let pollState; // local copy in case instance copy changes
        try {
            const value = await this.queueRead(VERY_FAST, 10);
            pollState = { type: 'poll', terminate: false, loop, subj: new BehaviorSubject(value) };
            this.monitorState = pollState;
        }
        catch (error) {
            this.provider.logger.error('Initial polling read failed', { error });
            throw error;
        }
        if (loop) {
            // start the polling loop
            void (async () => {
                for (;;) {
                    if (pollState.terminate) {
                        // terminate the loop, relaying error if given
                        if (typeof pollState.terminate === 'object') {
                            pollState.subj.error(pollState.terminate.error);
                        }
                        else {
                            pollState.subj.complete();
                        }
                        break;
                    }
                    // read values until an unrecoverable error happens
                    try {
                        pollState.subj.next(await this.queueRead(freq, 1));
                    }
                    catch (error) {
                        if (isRetriableLoraxErrorCode(getLoraxErrorCode(error))) {
                            this.provider.logger.warn('Polling read failed, retrying', { error });
                        }
                        else {
                            this.provider.logger.error('Fatal error in polling read', { error });
                            pollState.subj.error(error);
                            break;
                        }
                    }
                }
                if (this.monitorState === pollState) {
                    this.monitorState = undefined;
                }
            })();
        }
        return pollState.subj;
    }
    async unmonitor() {
        if (!this.monitorState) {
            this.provider.logger.warn(`No monitor was set up for ${this.key} when unmonitor() called`);
            return;
        }
        // Capture the value for use in `Promise` executor
        const state = this.monitorState;
        if (state.type === 'poll') {
            // Poll will complete its observable after `terminate` is set
            // Resolve when this happens, or reject if an unexpected error happens
            await new Promise((res, rej) => {
                state.subj.subscribe({ error: rej, complete: res });
                state.terminate = true;
            });
        }
        else {
            // Watch will complete its observable on successful termination
            try {
                await this.unwatch();
            }
            catch (error) {
                state.sub.unsubscribe();
                state.subj.error(error);
                throw error;
            }
        }
    }
    async queueRead(freq, maxAttempts) {
        const value = await this.provider.scheduler.queueRetriable({
            exec: async () => {
                if (!this.canRead) {
                    throw new ParamAccessDeniedError(this, 'read');
                }
                return await this.readRaw();
            },
            freq,
            desc: `read ${this.key}`,
            maxAttempts,
            retryFreq: freq * 10,
            fatalityChecker: errorHandling,
        });
        try {
            const decoded = this.codec.decode(value);
            return decoded;
        }
        catch (error) {
            this.logDecodeFail(value, error);
            throw error;
        }
    }
    async queueWriteAndReadback(v, noReadback) {
        this.writing = v;
        while (this.writing !== undefined) {
            let wrote;
            try {
                await this.provider.scheduler.queueRetriable({
                    exec: async () => {
                        if (!this.canWrite) {
                            throw new ParamAccessDeniedError(this, 'write');
                        }
                        wrote = this.writing;
                        await this.writeRaw(this.codec.encode(wrote));
                    },
                    freq: VERY_FAST,
                    desc: `write ${this.key}`,
                    maxAttempts: 10,
                    retryFreq: VERY_FAST,
                    fatalityChecker: errorHandling,
                });
            }
            finally {
                if (isEqual(wrote, this.writing)) {
                    this.writing = undefined;
                }
            }
        }
        if (this.canRead && !noReadback) {
            return await this.queueRead(VERY_FAST, 10);
        }
    }
    async queueOpen(freq, path) {
        await this.provider.scheduler.queueRetriable({
            exec: async () => {
                if (!this.canRead) {
                    throw new ParamAccessDeniedError(this, 'read');
                }
                this.watchFile = await this.provider.conn.loraxOpen(path);
            },
            freq,
            desc: `open ${this.key}`,
            maxAttempts: 10,
            retryFreq: VERY_FAST,
            fatalityChecker: errorHandling,
        });
    }
    async queueWatch(freq, maxAttempts) {
        const raw = await this.provider.scheduler.queueRetriable({
            exec: async () => {
                if (!this.canRead) {
                    throw new ParamAccessDeniedError(this, 'read');
                }
                if (!this.watchFile) {
                    throw new Error('Logic error: queueWatch() called without first opening file');
                }
                const maxBytes = (await this.provider.conn.getLoraxLimits()).maxPayload -
                    loraxWatchEventOverhead;
                const period = Math.round(4096 / freq); // Convert floating-point frequency to fixed-point period
                const obs = await this.watchFile.watch(period, 0, maxBytes);
                const onWatchEnded = () => (this.watchFile = undefined);
                return obs.pipe(tap({ error: onWatchEnded, complete: onWatchEnded }));
            },
            freq,
            desc: `watch ${this.key}`,
            maxAttempts,
            retryFreq: VERY_FAST,
            fatalityChecker: errorHandling,
        });
        return raw.pipe(map((value) => {
            try {
                return this.codec.decode(value);
            }
            catch (error) {
                this.logDecodeFail(value, error);
                throw error;
            }
        }));
    }
    async queueUnwatch(maxAttempts) {
        await this.provider.scheduler.queueRetriable({
            exec: async () => {
                if (!this.canRead) {
                    throw new ParamAccessDeniedError(this, 'read');
                }
                if (this.watchFile) {
                    await this.watchFile.close();
                }
            },
            freq: VERY_FAST,
            desc: `read ${this.key}`,
            maxAttempts,
            retryFreq: VERY_FAST,
            fatalityChecker: errorHandling,
        });
    }
    logDecodeFail = (value, error) => this.provider.logger.error(`Decoding ${hexString(value)} (${value.byteLength} bytes) for param ${this.key} failed`, { error });
}
class SimpleParam extends BaseParam {
    path;
    constructor(def, provider) {
        super(def, provider);
        if ('offset' in def) {
            this.path = { type: 'char', svc: pikachuService, char: pikaUuid(def.offset) };
        }
        else if ('path' in def) {
            this.path = { type: 'file', ...def };
        }
        else {
            this.path = { type: 'char', ...def };
        }
    }
    async readRaw() {
        if (this.path.type === 'char') {
            return await this.provider.conn.readChar(this.path.svc, this.path.char);
        }
        else {
            const limits = await this.provider.conn.getLoraxLimits();
            let accum = new Uint8Array(0);
            let offset = 0;
            for (;;) {
                const chunk = new Uint8Array(await this.provider.conn.loraxReadShort(this.path.path, offset, limits.maxPayload));
                accum = new Uint8Array([...accum, ...chunk]);
                if (chunk.byteLength < limits.maxPayload) {
                    break;
                }
                offset += chunk.byteLength;
            }
            return accum.buffer;
        }
    }
    async writeRaw(v) {
        if (this.path.type === 'char') {
            await this.provider.conn.writeCharWithResponse(this.path.svc, this.path.char, v);
        }
        else {
            // first see if WriteShort works
            try {
                await this.provider.conn.loraxWriteShort(this.path.path, v, 0, LoraxOpenFlags.truncate);
                return;
            }
            catch (err) {
                if (getLoraxErrorCode(err) !== EMSGSIZE) {
                    throw err;
                }
            }
            // Open the file instead; this handles the case of low MTU connections
            // Also, LittleFS files on Peach update atomically (from the perspective of other tasks using them)
            // when we close them. Opening the file for all multi-packet writes ensures coherence.
            // Before opening, check the file size and truncate only if needed (avoids ENOTSUP on Pikachu/Raichu)
            const existingSize = await this.provider.conn.loraxStatShort(this.path.path);
            const file = await this.provider.conn.loraxOpen(this.path.path, existingSize > v.byteLength ? LoraxOpenFlags.truncate : 0);
            try {
                for (let offset = 0; offset < v.byteLength; offset += file.maxWriteLength) {
                    await file.write(offset, v.slice(offset, offset + file.maxWriteLength));
                }
            }
            finally {
                await file.close();
            }
        }
    }
}
class IndexedParam extends BaseParam {
    path;
    char;
    index;
    indexCodec;
    indexChar;
    chunk;
    constructor(def, provider) {
        super(def, provider);
        this.path = { type: 'char', svc: pikachuService, char: pikaUuid(def.offset) };
        this.char = pikaUuid(def.offset);
        this.indexChar = pikaUuid(def.indexOffset);
        this.index = def.index;
        this.indexCodec = codecs[def.indexType];
        this.chunk = def.chunk;
    }
    async readRaw() {
        if (this.chunk) {
            const accum = new Uint8Array(this.chunk.total);
            for (let offset = 0; offset < this.chunk.total; offset += this.chunk.chunk) {
                await this.setIndex(offset);
                const chunk = new Uint8Array(await this.provider.conn.readChar(pikachuService, this.char));
                accum.set(chunk, offset);
            }
            return accum.buffer;
        }
        else {
            await this.setIndex(0);
            return await this.provider.conn.readChar(pikachuService, this.char);
        }
    }
    async writeRaw(v) {
        if (this.chunk) {
            for (let offset = 0; offset < v.byteLength; offset += this.chunk.chunk) {
                await this.setIndex(offset);
                await this.provider.conn.writeCharWithResponse(pikachuService, this.char, v.slice(offset, offset + this.chunk.chunk));
            }
        }
        else {
            await this.setIndex(0);
            await this.provider.conn.writeCharWithResponse(pikachuService, this.char, v);
        }
    }
    async setIndex(offset) {
        const raw = this.indexCodec.encode((offset << 16) | this.index);
        await this.provider.conn.writeCharWithResponse(pikachuService, this.indexChar, raw);
    }
}
export class ParamRegistry {
    devInfo;
    logger;
    conn;
    unlocker;
    scheduler;
    loraxWatchAckFreq = 1;
    loraxWatchAckRetryDelay = 100;
    disableWatch = false;
    params;
    loraxEventAckInProgress = new BehaviorSubject(false);
    subscriptions = [];
    constructor(devInfo, logger, conn, unlocker) {
        this.devInfo = devInfo;
        this.logger = logger;
        this.conn = conn;
        this.unlocker = unlocker;
        this.scheduler = new FairServiceScheduler();
        this.params = this.makeParams();
        this.connectLoraxEventAck();
        this.connectLoraxSchedulerConfig();
    }
    /** Close all subscriptions */
    teardown() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        for (const paramSet of this.params.values()) {
            for (const param of paramSet) {
                param.teardown();
            }
        }
    }
    get(key) {
        const params = this.params.get(key);
        if (!params) {
            this.logger.warn(`Could not find param ${key}`);
        }
        return (params ?? []);
    }
    getInApiTry(key) {
        return this.devInfo.findInApi(this.get(key));
    }
    getInApiThrow(key) {
        const param = this.devInfo.findInApi(this.params.get(key) ?? []);
        if (!param) {
            throw new Error(`Could not find param ${key} in API ${asString(this.devInfo.info)}`);
        }
        return param;
    }
    keys() {
        return this.params.keys();
    }
    entries() {
        return this.params.entries();
    }
    values() {
        return this.params.values();
    }
    get isAvailable() {
        return !!(this.unlocker.unlocked && this.devInfo.info);
    }
    get isAvailableObs() {
        return combineLatest([this.unlocker.unlockedObs, this.devInfo.infoObs]).pipe(map(([level, version]) => !!(level && version)), distinctUntilChanged());
    }
    makeParams() {
        const params = new Map();
        for (const def of defs) {
            const param = 'type' in def && def.type === 'indexed'
                ? new IndexedParam(def, this)
                : new SimpleParam(def, this);
            const arr = params.get(def.key) ?? [];
            params.set(def.key, arr);
            arr.push(param);
        }
        return params;
    }
    connectLoraxEventAck() {
        this.subscriptions.push(combineLatest([this.conn.loraxUnackedEventCountObs, this.loraxEventAckInProgress])
            .pipe(map(([count, refreshing]) => !!count && !refreshing), distinctUntilChanged(), filter((v) => v))
            .subscribe(() => void (async () => {
            try {
                this.loraxEventAckInProgress.next(true);
                await this.scheduler.queue(() => this.conn.loraxAckEvents(), this.loraxWatchAckFreq);
            }
            catch (error) {
                if (getLoraxErrorCode(error) !== EPIPE) {
                    this.logger.warn('Lorax watch ack failed', { error });
                }
                await firstValueFrom(timer(this.loraxWatchAckRetryDelay));
            }
            finally {
                this.loraxEventAckInProgress.next(false);
            }
        })()));
    }
    connectLoraxSchedulerConfig() {
        // On connection, increase the jobs count when connected to a Lorax device
        this.subscriptions.push(this.conn.deviceStateObs
            .pipe(distinctUntilChanged(), filter((v) => v === 'connected'))
            .subscribe(() => void (async () => {
            if (this.conn.isLoraxConnected) {
                try {
                    const limits = await this.conn.getLoraxLimits();
                    this.scheduler.nThreads = limits.maxInFlightCommands;
                    return;
                }
                catch (error) {
                    this.logger.warn('Failed to get Lorax limits for scheduler', {
                        error,
                    });
                }
            }
            this.scheduler.nThreads = 1;
        })()));
    }
}
//# sourceMappingURL=param-registry.js.map